import type { ButtonActionProps } from '@sparklayer-io/spark-dash-ui/components/ui/button/types';
import { useModal } from '@sparklayer-io/spark-dash-ui/hooks/use-modal';
import { Alert, Button, Card, EmptyState, LearnMoreLink, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../../components/spark-suspense';
import { featureEnabled, features } from '../../../../constants/features';
import { useEnv } from '../../../../context/env-context';
import { useHandlers, useSite, useUser, withPageProviders } from '../../../../context/page-context';
import type { TeamInviteApiResponse, TeamMemberApiResponse } from '../../types';
import { AccountTeamsTable } from '../components/account-teams-table';
import { DeleteInviteModal } from '../components/modals/delete-invite-modal';
import { RemoveMemberModal } from '../components/modals/remove-member-modal';
import { SwapOwnerModal } from '../components/modals/swap-owner-modal';
import { OwnerDetails } from '../components/owner-details';
import { useSettingsTeam } from '../hooks/use-account-team';

const TEAM_INVITE_PAGE_PATH = '/settings/team/invite';

export const SettingsTeamPage = withPageProviders(SettingsTeamPageContent);

function SettingsTeamPageContent() {
  const { navigationHandler } = useHandlers();
  const { data, isLoading, isError, refetch } = useSettingsTeam();
  const { isSparkStaff, manageAccount } = useUser();
  const { userId } = useSite();
  const { team } = useEnv();

  const isOwner = data ? userId === data.owner.id || !!isSparkStaff : false;
  const rolesEnabled = featureEnabled(features.TEAM_MEMBER_ROLE, team.slugged_name);

  // Modals
  const [memberToRemove, setMemberToRemove] = useState<TeamMemberApiResponse | null>(null);
  const {
    isOpen: isRemoveMemberModalOpen,
    openModal: openRemoveMemberModal,
    closeModal: closeRemoveMemberModal,
  } = useModal();

  const [inviteToDelete, setInviteToDelete] = useState<TeamInviteApiResponse | null>(null);
  const {
    isOpen: isDeleteInviteModalOpen,
    openModal: openDeleteInviteModal,
    closeModal: closeDeleteInviteModal,
  } = useModal();

  const [ownerCandidate, setOwnerCandidate] = useState<TeamMemberApiResponse | null>(null);
  const {
    isOpen: isSwapOwnerModalOpen,
    openModal: openSwapOwnerModal,
    closeModal: closeSwapOwnerModal,
  } = useModal();

  const pagePrimaryAction: ButtonActionProps = {
    label: 'Invite',
    icon: 'userAdd',
    onClick: () => {
      navigationHandler.navigate(TEAM_INVITE_PAGE_PATH, { excludeSiteEnv: true });
    },
  };

  if (!(manageAccount || isOwner)) {
    return (
      <Page title={t('features:account.pages.settings_team_page.title')}>
        <Alert variant="warning" headingLevel="h4">
          {t('features:account.pages.settings_team_page.no_access')}
        </Alert>
      </Page>
    );
  }

  return (
    <Page
      title={t('features:account.pages.settings_team_page.title')}
      description={t('features:account.pages.settings_team_page.subtitle')}
      learnMoreUrl="https://docs.sparklayer.io/account-settings#nQ3DC"
      primaryAction={isOwner ? pagePrimaryAction : undefined}
    >
      <SparkSuspense
        isLoading={isLoading}
        isError={isError}
        errorMessage={t('errors:generic')}
        onReload={() => refetch()}
      >
        {data && (
          <>
            <Card>
              <Card.Header headingLevel="h2">
                {t('features:account.pages.settings_team_page.owner.header')}
              </Card.Header>
              <Card.Body>
                <OwnerDetails owner={data.owner} companyName={team.name} />
              </Card.Body>
            </Card>
            <Card>
              <Card.Header headingLevel="h2">
                {t('features:account.pages.settings_team_page.members.header')}
              </Card.Header>
              <Card.Body>
                {data.users.length === 0 && data.invites.length === 0 ? (
                  <TeamMembersEmptyState
                    onInvite={
                      isOwner
                        ? () =>
                            navigationHandler.navigate(TEAM_INVITE_PAGE_PATH, {
                              excludeSiteEnv: true,
                            })
                        : undefined
                    }
                  />
                ) : (
                  <>
                    <Text>
                      {t('features:account.pages.settings_team_page.invite_member')}{' '}
                      <LearnMoreLink link="https://docs.sparklayer.io/account-settings#nQ3DC" />
                    </Text>

                    <AccountTeamsTable
                      team={data}
                      isViewerOwner={isOwner}
                      rolesEnabled={rolesEnabled}
                      onSwapOwner={(member) => {
                        setOwnerCandidate(member);
                        openSwapOwnerModal();
                      }}
                      onDeleteInvite={(invite) => {
                        setInviteToDelete(invite);
                        openDeleteInviteModal();
                      }}
                      onRemoveMember={(member) => {
                        setMemberToRemove(member);
                        openRemoveMemberModal();
                      }}
                    ></AccountTeamsTable>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Modals */}
            <RemoveMemberModal
              member={memberToRemove}
              isOpen={isRemoveMemberModalOpen}
              closeModal={closeRemoveMemberModal}
            />

            <DeleteInviteModal
              invite={inviteToDelete}
              isOpen={isDeleteInviteModalOpen}
              closeModal={closeDeleteInviteModal}
            />

            <SwapOwnerModal
              member={ownerCandidate}
              isOpen={isSwapOwnerModalOpen}
              closeModal={closeSwapOwnerModal}
            />
          </>
        )}
      </SparkSuspense>
    </Page>
  );
}

function TeamMembersEmptyState({ onInvite }: { onInvite?: () => void }) {
  return (
    <EmptyState
      title={t('features:account.pages.settings_team_page.empty.title')}
      subtitle={t('features:account.pages.settings_team_page.empty.subtitle')}
      imageType="customerGroup"
    >
      {onInvite && (
        <Button icon="userAdd" onClick={onInvite}>
          {t('features:account.pages.settings_team_page.empty.invite_member_button')}
        </Button>
      )}
    </EmptyState>
  );
}
